import { useEffect, useState } from "react";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export type ProgressRingSize = "sm" | "md" | "lg";

export interface ProgressRingProps {
  value: number;
  size?: ProgressRingSize;
  /** Hex/rgb color for the progress arc; defaults to brand-purple */
  color?: string;
  /** Hex/rgb color for the track ring; defaults to gray-200 / dark gray-700 */
  trackColor?: string;
  /** Center text; defaults to rounded value + "%" */
  label?: string;
  strokeWidth?: number;
  className?: string;
}

// ── Size tokens ───────────────────────────────────────────────────────────────

const SIZE_PX: Record<ProgressRingSize, number>      = { sm: 48, md: 80, lg: 120 };
const DEFAULT_STROKE: Record<ProgressRingSize, number> = { sm: 4,  md: 6,  lg: 8  };
const FONT_SIZE: Record<ProgressRingSize, number>    = { sm: 10, md: 16, lg: 24  };

// ── Component ─────────────────────────────────────────────────────────────────

export function ProgressRing({
  value,
  size = "md",
  color,
  trackColor,
  label,
  strokeWidth,
  className,
}: ProgressRingProps) {
  // Animate from 0 on mount
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() =>
      setProgress(Math.min(100, Math.max(0, value))),
    );
    return () => cancelAnimationFrame(frame);
  }, [value]);

  const px          = SIZE_PX[size];
  const sw          = strokeWidth ?? DEFAULT_STROKE[size];
  const r           = (px - sw) / 2;
  const cx          = px / 2;
  const cy          = px / 2;
  const circumference = 2 * Math.PI * r;
  const offset      = circumference * (1 - progress / 100);
  const isComplete  = value >= 100;
  const displayLabel = label ?? `${Math.round(value)}%`;

  // Use currentColor via className when no explicit prop so dark mode adapts
  const trackStroke  = trackColor ?? "currentColor";
  const trackClass   = trackColor ? "" : "text-zinc-200 dark:text-zinc-600";

  const arcStroke    = isComplete ? "currentColor" : (color ?? "currentColor");
  const arcClass     = isComplete
    ? "text-yellow-400 dark:text-yellow-500"
    : (color ? "" : "text-brand-purple dark:text-violet-400");

  return (
    <div
      className={cn("relative inline-flex items-center justify-center", className)}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      role="progressbar"
      aria-label={displayLabel}
    >
      {/* Pulsing ring at 100% */}
      {isComplete && (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full ring-2 ring-yellow-400 dark:ring-yellow-500 animate-ping opacity-40"
        />
      )}

      <svg width={px} height={px}>
        {/* Track */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={sw}
          stroke={trackStroke}
          className={trackClass}
        />

        {/* Progress arc */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth={sw}
          stroke={arcStroke}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
          className={arcClass}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
            transition: "stroke-dashoffset 600ms ease-out",
          }}
        />

        {/* Center label */}
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={FONT_SIZE[size]}
          fontWeight="600"
          style={{ fill: "var(--color-content-primary, #0F172A)" }}
        >
          {displayLabel}
        </text>
      </svg>
    </div>
  );
}

ProgressRing.displayName = "ProgressRing";
